// DTO de `GET /api/supervisor/dashboard` (S1 — pantalla principal del rol).
//
// Diseño: visibilidad gobernada por SCOPE ESTRUCTURAL (planta asignada en
// `usuarios_supervisor_plantas`), NO por programación del día. La programación
// es contexto operativo para 3 estados de UI:
//
//   1. Con programación HOY → dashboard normal + acciones (validar, delegar)
//   2. Sin programación HOY → dashboard funcional + banner + acciones disabled
//   3. Sin scope (no en usuarios_supervisor_plantas) → estado vacío
//
// Las líneas vienen ORDENADAS por prioridad operativa desde el backend
// (paro_activo > rojo > amarillo > sin_data > verde > sin captura). El
// frontend NO re-ordena.
import { z } from 'zod';
import { lineaSchema } from './linea.js';
import { conectividadLecturaSchema } from './ingesta_maquina.js';
import { unidadMedidaSchema } from './unidad_medida.js';
export const estadoSemaforoSchema = z.enum(['rojo', 'amarillo', 'verde', 'sin_data']);
/**
 * KPI de una línea para HOY.
 *   - `cumplimiento_pct = null` → falta capacidad estándar (sin_data)
 *   - `estado_semaforo = 'sin_data'` → línea capturó pero su SKU no tiene
 *     capacidad configurada en `skus_lineas`. Problema operativo a resolver.
 *
 * Si la línea está en el scope pero NO se capturó NADA hoy, el endpoint
 * devuelve `kpi: null` (ver `supervisorLineaSchema`).
 */
export const supervisorKpiLineaSchema = z.object({
    cantidad_real: z.number(),
    bloques_capturados: z.number().int(),
    capacidad_estandar_hora: z.number().nullable(),
    meta_acumulada: z.number().nullable(),
    cumplimiento_pct: z.number().nullable(),
    estado_semaforo: estadoSemaforoSchema,
    paros_count: z.number().int(),
    paros_min_totales: z.number(),
    paro_activo: z.boolean(),
    /**
     * Minutos desde que el paro activo empezó (NOW() - hora_inicio). null si no
     * hay paro activo. Sirve para mostrar "Paro 72m sin cerrar" sin que el FE
     * tenga que recalcularlo.
     */
    paro_activo_minutos: z.number().int().nullable(),
    /**
     * Causal jerárquica del paro activo: "{madre} · {específica}". null si no
     * hay paro activo. Si hay varios paros abiertos simultáneamente (caso raro),
     * se devuelve el del paro más reciente.
     */
    paro_activo_causal: z.string().nullable(),
    sku_predominante: z
        .object({
        id: z.string().uuid(),
        codigo_cliente: z.string(),
        descripcion: z.string(),
    })
        .nullable(),
    lider: z
        .object({
        id: z.string().uuid(),
        nombre: z.string(),
        apellido: z.string(),
    })
        .nullable(),
    ultimo_bloque: z.string().nullable(),
    /**
     * Minutos desde la última captura del turno. null si no hay captura aún.
     * El FE muestra "Captura ok · hace 4 min" o "Sin captura 83 min" según
     * un umbral (>=70 min sin captura = alerta).
     */
    minutos_desde_ultima_captura: z.number().int().nullable(),
    /**
     * Unidad de cantidad_real y meta: 'kg' si todo lo capturado en el turno es
     * de SKUs GRANEL; si no 'paq'. Opcional para no romper clientes previos.
     */
    unidad: unidadMedidaSchema.optional(),
    /**
     * R12 (opcional): true si la línea está conectada a máquina (híbrida o
     * máquina) y este KPI se calculó en vivo sobre los registros, sin esperar el
     * refresco de la vista materializada. Ausente en líneas manuales.
     */
    en_vivo: z.literal(true).optional(),
});
/**
 * Una línea del dashboard. `kpi` es null cuando hay 0 bloques capturados HOY
 * (línea visible pero "esperando captura"). Distinto de `estado_semaforo:
 * 'sin_data'` (que SÍ tiene captura pero falta config).
 */
export const supervisorLineaSchema = z.object({
    linea: lineaSchema,
    kpi: supervisorKpiLineaSchema.nullable(),
    /**
     * Ingesta de máquina (opcional): null en líneas manuales. Va en la línea y
     * no en `kpi` porque `kpi` es null sin capturas, justo cuando una línea de
     * máquina sin señal más lo necesita.
     */
    ...conectividadLecturaSchema.shape,
});
export const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
export const programacionHoySchema = z.object({
    activa: z.boolean(),
    turno_id: z.string().uuid().nullable(),
    turno_numero: z.number().int().nullable(),
    turno_nombre: z.string().nullable(),
    planta_id: z.string().uuid().nullable(),
});
/**
 * Fuente de cómo se resolvió `turno_objetivo`:
 *   - `programacion_hoy`: el supervisor tiene programación cargada para hoy
 *   - `turno_actual`: sin programación, pero el clock cae dentro de algún turno
 *   - `sin_turno`: ni programación ni clock dentro de turno (ej. fin de semana)
 *
 * El KPI del dashboard SIEMPRE se filtra por `turno_objetivo.id`. Si la fuente
 * es `sin_turno`, todas las líneas vienen con `kpi: null` (no se puede mostrar
 * "del turno" si no hay turno).
 */
export const fuenteTurnoObjetivoSchema = z.enum(['programacion_hoy', 'turno_actual', 'sin_turno']);
export const turnoObjetivoSchema = z.object({
    id: z.string().uuid().nullable(),
    numero: z.number().int().min(1).max(4).nullable(),
    nombre: z.string().nullable(),
    fuente: fuenteTurnoObjetivoSchema,
    /** "HH:MM" del rango horario del turno. null si fuente === 'sin_turno'. */
    hora_inicio: z.string().nullable(),
    hora_fin: z.string().nullable(),
    /**
     * Si el turno cruza medianoche (T3). S5 lo necesita para componer los
     * timestamps de paros con combinarFechaHoraTurno (+1 día en la cola
     * post-medianoche). El backend siempre lo resuelve (false si sin_turno).
     */
    cruza_medianoche: z.boolean(),
});
// ===========================================================================
// KPIs agregados del turno — visibles arriba del grid de líneas
// ===========================================================================
/**
 * Capturas del turno objetivo. Pendientes = válidas con validado_at NULL.
 * Validados = validado_at NOT NULL. Delegadas = capturado_por_supervisor=TRUE.
 * No se solapan (las 3 son visibles para el supervisor — un registro delegado
 * cuenta para "delegadas" Y para pendiente/validada según su estado).
 */
export const supervisorCapturasAgregadasSchema = z.object({
    pendientes: z.number().int(),
    validados: z.number().int(),
    delegadas: z.number().int(),
});
/** Incidencias del scope del supervisor para HOY (todas las líneas en su scope). */
export const supervisorIncidenciasAgregadasSchema = z.object({
    activas: z.number().int(),
    resueltas: z.number().int(),
});
/**
 * Comparativa de cumplimiento agregado: % del cumplimiento promedio
 * (cantidad_real / meta) sobre líneas del scope. `vs_ayer_pct` y
 * `vs_semana_pasada_pct` son delta absoluto (pct hoy - pct referencia). Si la
 * referencia no tiene datos, viene null.
 */
export const supervisorComparativaSchema = z.object({
    cumplimiento_hoy_pct: z.number().nullable(),
    vs_ayer_pct: z.number().nullable(),
    vs_semana_pasada_pct: z.number().nullable(),
    /**
     * Cantidad total producida hoy (suma de todas las líneas en scope, todos
     * los turnos válidos del día). Útil para el banner "Comparativa" al pie.
     */
    cantidad_hoy_total: z.number().nullable(),
    /**
     * Cantidad total de la misma fecha hace 7 días (referencia "semana pasada").
     * null si no hay datos para ese día.
     */
    cantidad_semana_pasada_total: z.number().nullable(),
    /**
     * Delta de cantidad vs semana pasada en porcentaje relativo:
     * (hoy - sem_pasada) / sem_pasada * 100. null si no hay referencia.
     */
    cantidad_vs_semana_pasada_pct: z.number().nullable(),
    /** Minutos totales de paros HOY en todas las líneas del scope. */
    paros_minutos_hoy_total: z.number(),
});
export const supervisorKpisAgregadosSchema = z.object({
    capturas: supervisorCapturasAgregadasSchema,
    incidencias: supervisorIncidenciasAgregadasSchema,
    comparativa: supervisorComparativaSchema,
});
// ===========================================================================
// Incidencias activas del scope — para la lista con CTA "Resolver"
// ===========================================================================
export const tipoIncidenciaSchema = z.enum([
    'P1_sin_captura',
    'P2_paro_largo',
    'P3_sku_distinto',
    'P4_operarios_inusual',
    'P5_fuera_turno',
    'P6_baja_capacidad',
    'P7_sin_senal_maquina',
]);
export const severidadIncidenciaSchema = z.enum(['info', 'warning', 'critical']);
export const supervisorIncidenciaActivaSchema = z.object({
    id: z.string().uuid(),
    tipo: tipoIncidenciaSchema,
    severidad: severidadIncidenciaSchema,
    detectada_at: z.string().datetime({ offset: true }),
    /** Minutos desde detección (NOW() - detectada_at). */
    minutos_abierta: z.number().int(),
    linea_id: z.string().uuid().nullable(),
    linea_nombre: z.string().nullable(),
    /**
     * Descripción humana de la incidencia. Se compone en backend desde
     * contexto_json + tipo + duración. Ej: "Paro 72m · falla sellado".
     */
    descripcion: z.string(),
});
// ===========================================================================
// Programación semanal de líderes — preview read-only en S1
// ===========================================================================
export const supervisorProgramacionItemSchema = z.object({
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    turno_numero: z.number().int().min(1).max(4),
    lider: z
        .object({
        id: z.string().uuid(),
        nombre: z.string(),
        apellido: z.string(),
    })
        .nullable(),
});
export const supervisorProgramacionSemanalSchema = z.object({
    semana_iso: z.string(),
    /** Rango humano "Lun 19 — Dom 25" calculado en backend. */
    rango_humano: z.string(),
    items: z.array(supervisorProgramacionItemSchema),
});
// ===========================================================================
// PATCH /api/supervisor/incidencias/:id/resolver
// ===========================================================================
export const supervisorResolverIncidenciaSchema = z.object({
    accion_tomada: z
        .string()
        .trim()
        .min(5, 'Describa la acción (mínimo 5 caracteres)')
        .max(500, 'Máximo 500 caracteres'),
});
export const supervisorIncidenciaResueltaSchema = z.object({
    id: z.string().uuid(),
    tipo: tipoIncidenciaSchema,
    severidad: severidadIncidenciaSchema,
    estado: z.enum(['pendiente', 'en_revision', 'resuelta', 'descartada']),
    resuelta_at: z.string().datetime({ offset: true }).nullable(),
    resuelta_por_user_id: z.string().uuid().nullable(),
    accion_tomada: z.string().nullable(),
});
export const supervisorDashboardSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(plantaResumenSchema),
    }),
    programacion_hoy: programacionHoySchema,
    /**
     * Turno con el que se filtró el KPI de cada línea. Su `fuente` indica de
     * dónde salió: programación, clock del servidor (tenant tz) o ninguno.
     */
    turno_objetivo: turnoObjetivoSchema,
    lineas: z.array(supervisorLineaSchema),
    /** KPIs agregados del turno (capturas, incidencias, comparativa). */
    kpis_agregados: supervisorKpisAgregadosSchema,
    /** Incidencias abiertas del scope HOY, ordenadas por severidad+detección. */
    incidencias_activas: z.array(supervisorIncidenciaActivaSchema),
    /** Programación de líderes de la semana ISO actual (preview read-only). */
    programacion_semanal: supervisorProgramacionSemanalSchema,
    hora_servidor: z.string(),
    fecha_hoy: z.string(),
    /**
     * R12 (opcional): true si alguna línea del scope está conectada a máquina;
     * el dashboard consulta entonces cada 15 s en lugar de cada 30 s.
     */
    tiene_lineas_maquina: z.boolean().optional(),
});
//# sourceMappingURL=supervisor_dashboard.js.map